import Phaser from 'phaser';
import { COLORS, DEPTHS, GAME_HEIGHT, GAME_WIDTH, MENU_BAR } from '../config/gameConfig';
import { InboxApp } from '../apps/InboxApp';
import { SlackApp } from '../apps/SlackApp';
import { OPENING_STATE } from '../content/opening-state';
import { PROCEDURAL_EMAIL_POOL } from '../content/emails';
import { JAMIE_MESSAGES } from '../content/slack-messages';
import {
  Clock,
  formatFictionalTime,
  FICTION_START_MINUTES,
  FICTION_MINUTES_SPAN,
  RUNTIME_MS,
} from '../systems/Clock';
import { EventBus } from '../systems/EventBus';
import { JamieSystem } from '../systems/JamieSystem';
import { ProceduralEvents } from '../systems/ProceduralEvents';
import { AudioManager } from '../systems/AudioManager';
import { MenuBar } from '../ui/MenuBar';
import { AgingSpreadsheetApp } from '../apps/AgingSpreadsheetApp';
import { FieldGuideApp } from '../apps/FieldGuideApp';
import { PaymentsApp } from '../apps/PaymentsApp';
import { PAYMENTS_WINDOW } from '../config/gameConfig';
import { Tooltip } from '../ui/Tooltip';
import { BeatsSystem } from '../systems/BeatsSystem';
import { TeachPanel } from '../ui/TeachPanel';
import { DunningModal } from '../ui/DunningModal';
import { CashApplicationModal } from '../ui/CashApplicationModal';
import { ShortPayModal } from '../ui/ShortPayModal';
import { DisputeFormModal } from '../ui/DisputeFormModal';
import { CollectionsCallModal } from '../ui/CollectionsCallModal';
import { WriteOffModal } from '../ui/WriteOffModal';
import { DsoDashboardModal } from '../ui/DsoDashboardModal';
import { ScriptedEmailsSystem } from '../systems/ScriptedEmailsSystem';
import { SessionTracker } from '../systems/SessionTracker';
import { CfoSystem } from '../systems/CfoSystem';
import { SCRIPTED_EMAILS } from '../content/scripted-emails';
import { BEATS } from '../content/beats';
import { ATLAS_TRADING_CALL } from '../content/collections-call';
import { CFO_MESSAGES } from '../content/cfo-messages';
import { WindowManager } from '../systems/WindowManager';
import { TaskBar } from '../ui/TaskBar';
import { SaveStore } from '../systems/SaveStore';
import { ARLedger } from '../systems/ARLedger';
import { SettingsModal } from '../ui/SettingsModal';
import { WelcomeCard } from '../ui/WelcomeCard';
import type { RunMode } from './MenuScene';

interface DesktopData {
  mode?: RunMode;
}

const PROCEDURAL_EMAIL_INTERVAL_MS = 10_000;

export class DesktopScene extends Phaser.Scene {
  private bus!: EventBus;
  private clock!: Clock;
  private procedural!: ProceduralEvents;
  private jamie!: JamieSystem;
  private beats!: BeatsSystem;
  private inboxApp!: InboxApp;
  private slackApp!: SlackApp;
  private spreadsheetApp!: AgingSpreadsheetApp;
  private teachPanel!: TeachPanel;
  private dunningModal!: DunningModal;
  private cashAppModal!: CashApplicationModal;
  private shortPayModal!: ShortPayModal;
  private disputeFormModal!: DisputeFormModal;
  private collectionsCallModal!: CollectionsCallModal;
  private writeOffModal!: WriteOffModal;
  private dsoDashboardModal!: DsoDashboardModal;
  private scriptedEmails!: ScriptedEmailsSystem;
  private sessionTracker!: SessionTracker;
  private cfo!: CfoSystem;
  private windowManager!: WindowManager;
  private taskBar!: TaskBar;
  private fieldGuideApp!: FieldGuideApp;
  private paymentsApp!: PaymentsApp;
  private tooltip!: Tooltip;
  private saveStore!: SaveStore;
  private arLedger!: ARLedger;
  private settingsModal!: SettingsModal;
  private runMode: RunMode = 'core';
  private endlessEnded = false;

  constructor() {
    super({ key: 'DesktopScene' });
  }

  create(data?: DesktopData): void {
    const mode: RunMode = data?.mode ?? 'core';
    this.runMode = mode;
    this.endlessEnded = false;
    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, COLORS.desktopBackground)
      .setOrigin(0, 0)
      .setDepth(DEPTHS.desktop);

    this.bus = new EventBus();
    const getNowMs = () => this.time.now;

    this.clock = new Clock({ bus: this.bus, getNowMs });
    this.sessionTracker = new SessionTracker({ bus: this.bus, clock: this.clock });
    this.saveStore = new SaveStore();
    this.arLedger = new ARLedger(this.bus);
    this.settingsModal = new SettingsModal(this, this.bus);
    // Record term unlocks to persistent save for cross-session retention.
    this.bus.on('term:unlocked', ({ term }) => {
      const unlock = this.sessionTracker.snapshot().termUnlocks.get(term);
      if (unlock) this.saveStore.recordUnlock(term, unlock.fictionalMinutes, unlock.beatId);
    });

    this.windowManager = new WindowManager({ bus: this.bus });
    this.windowManager.register('inbox');
    this.windowManager.register('slack');
    this.windowManager.register('spreadsheet');
    this.windowManager.register('field-guide');
    this.windowManager.register('payments');

    this.bus.on('email:received', () => AudioManager.getInstance().playEmailChime());
    this.bus.on('slack:message-received', ({ message }) => {
      const audio = AudioManager.getInstance();
      if (message.author === 'jamie') audio.playSlackChimeJamie();
      else if (message.author === 'cfo') audio.playSlackChimeCfo();
      else if (message.author === 'marcus') audio.playSlackChimeMarcus();
    });
    this.bus.on('teach:requested', () => AudioManager.getInstance().playTeachPanelChime());

    // Ambient office hum begins at desktop scene start; stops at day:ended.
    AudioManager.getInstance().startAmbient();

    this.procedural = new ProceduralEvents({
      bus: this.bus,
      getNowMs,
      pool: PROCEDURAL_EMAIL_POOL,
      rng: Math.random,
      intervalMs: PROCEDURAL_EMAIL_INTERVAL_MS,
      runtimeMs: RUNTIME_MS,
      formatTimeLabel: (nowMs) => {
        const fictional = FICTION_START_MINUTES + (Math.min(nowMs, RUNTIME_MS) / RUNTIME_MS) * FICTION_MINUTES_SPAN;
        return formatFictionalTime(fictional);
      },
      unrestricted: mode === 'endless',
      // Aging unlocks once beat-1 has fired in core mode; always in endless.
      agingUnlocked: () => mode === 'endless' || this.sessionTracker.snapshot().termUnlocks.has('aging-bucket'),
      callsUnlocked: () => mode === 'endless' || this.sessionTracker.snapshot().termUnlocks.has('promise-to-pay'),
      paymentsUnlocked: () => mode === 'endless' || this.sessionTracker.snapshot().termUnlocks.has('cash-application'),
    });
    // Apply persisted rate preferences immediately and live-update on changes.
    this.procedural.setRates(this.settingsModal.initialRates());
    this.bus.on('settings:rates-changed', (p) => this.procedural.setRates(p.rates));

    // Welcome card: pauses procedural events while it's up so first-time
    // players can look around without things piling on. Dismiss "Begin"
    // resumes at the player's persisted rate; "Take it slow" halves all
    // rates and persists that preference.
    if (mode === 'core') {
      const persisted = this.settingsModal.initialRates();
      this.procedural.setRates({ ...persisted, paused: true });
      new WelcomeCard(this, this.bus, (choice) => {
        if (choice === 'slow') {
          const slow = {
            email: 0.5,
            payment: 0.5,
            'aging-tick': 0.5,
            call: 0.5,
            paused: false,
          };
          this.bus.emit('settings:rates-changed', { rates: slow });
        } else {
          this.bus.emit('settings:rates-changed', {
            rates: { ...persisted, paused: false },
          });
        }
      });
    }

    // Procedural call rings → ringtone audio + voicemail email arrives in inbox
    // (the player can't pick up the call directly — calls become triage decisions).
    let voicemailCounter = 0;
    this.bus.on('procedural:call-rang', (p) => {
      AudioManager.getInstance().playPhoneRing();
      voicemailCounter += 1;
      const fictional = FICTION_START_MINUTES + (Math.min(getNowMs(), RUNTIME_MS) / RUNTIME_MS) * FICTION_MINUTES_SPAN;
      this.bus.emit('email:received', {
        email: {
          id: `vm-${voicemailCounter}`,
          subject: `Voicemail: ${p.caller}`,
          from: 'voicemail@cascadefoods.com',
          body: `${p.caller} left a voicemail. They asked you to call back about an outstanding invoice.`,
          receivedAt: formatFictionalTime(fictional),
          read: false,
          category: 'voicemail',
        },
      });
    });
    // Procedural aging-tick → faint chime so the player notices.
    this.bus.on('aging-tick:fired', () => AudioManager.getInstance().playKeyboardClack());
    // Wire arrival chime.
    this.bus.on('payment:queued', () => AudioManager.getInstance().playEmailChime());

    // Empathy beats: Jamie reacts to score swings (one ping per threshold).
    const jamieSeenThresholds = new Set<string>();
    this.bus.on('score:changed', (p) => {
      const fire = (key: string, body: string): void => {
        if (jamieSeenThresholds.has(key)) return;
        jamieSeenThresholds.add(key);
        this.bus.emit('slack:message-received', {
          message: {
            id: `jamie-react-${key}`,
            author: 'jamie',
            body,
            fictionalMinutes: 0,
          },
        });
      };
      if (p.score <= -10) fire('low-10', "hey — i see it stacking up. focus the 90+ aging customers first. they're what kills DSO.");
      if (p.score <= -25) fire('low-25', "happens. take a breath. one Acme write-off won't tank you, ignoring it will.");
      if (p.score >= 30) fire('high-30', "nice rhythm. keep the cash apps moving.");
      if (p.score >= 60) fire('high-60', "you're cooking. priya's gonna stop checking in if you keep this up.");
    });

    // Beat-3 turns into a queued scripted payment (Peak Industries / W-2093 / $18K).
    // Player has to click "Apply" in the Payments window — empathy spec: pile waits for engagement.
    this.bus.on('beat:triggered', (p) => {
      if (p.beatId !== 'beat-3-cash-app') return;
      const now = getNowMs();
      this.bus.emit('payment:queued', {
        payment: {
          id: 'W-2093',
          customer: 'Peak Industries',
          amount: 18000,
          remittance: 'Peak Industries — May invoices',
          arrivedAtMs: now,
          expiresAtMs: now + PAYMENTS_WINDOW.expirationMs,
        },
      });
    });

    this.jamie = new JamieSystem({ bus: this.bus, messages: JAMIE_MESSAGES });
    this.cfo = new CfoSystem({ bus: this.bus, messages: CFO_MESSAGES });
    // In endless mode, don't dispatch scripted beats or scripted emails — pure procedural pressure.
    this.beats = new BeatsSystem({ bus: this.bus, beats: mode === 'endless' ? [] : BEATS });
    this.scriptedEmails = new ScriptedEmailsSystem({
      bus: this.bus,
      emails: mode === 'endless' ? [] : SCRIPTED_EMAILS,
    });

    new MenuBar(this, this.bus);
    this.tooltip = new Tooltip(this);
    // TeachPanel always exists — every "What is X? ↗" link relies on it
    // and players need definitions in every mode, not just core.
    this.teachPanel = new TeachPanel(this, this.bus, this.tooltip);
    this.dunningModal = new DunningModal(this, this.bus, this.tooltip, this.arLedger);
    this.cashAppModal = new CashApplicationModal(this, this.bus, this.tooltip, this.arLedger);
    this.shortPayModal = new ShortPayModal(this, this.bus);
    this.disputeFormModal = new DisputeFormModal(this, this.bus, this.tooltip);
    this.collectionsCallModal = new CollectionsCallModal(this, this.bus, ATLAS_TRADING_CALL);
    this.writeOffModal = new WriteOffModal(this, this.bus);
    this.dsoDashboardModal = new DsoDashboardModal(this, this.bus, this.sessionTracker);
    // Create spreadsheet first → sits at the back; Slack next; Inbox last → on top.
    // Spec §5 opening state: Inbox and AR Spreadsheet both visible, Inbox on top.
    this.spreadsheetApp = new AgingSpreadsheetApp(this, this.bus, this.arLedger);
    this.slackApp = new SlackApp(this, this.bus, this.tooltip);
    this.inboxApp = new InboxApp(this, this.bus, OPENING_STATE.emails, this.tooltip);
    this.inboxApp.y = Math.max(this.inboxApp.y, MENU_BAR.height + 8);
    this.fieldGuideApp = new FieldGuideApp(this, this.bus, this.sessionTracker);
    this.paymentsApp = new PaymentsApp(this, this.bus, { getNowMs });

    this.taskBar = new TaskBar(this, this.bus, this.windowManager);

    // Field Guide starts minimized — it's a reference, not a primary surface.
    this.bus.emit('window:minimize-requested', { windowId: 'field-guide' });

    this.input.keyboard?.on('keydown-ESC', () => {
      if (!this.scene.isPaused('DesktopScene')) {
        this.scene.pause();
        this.scene.launch('PauseScene');
      }
    });

    this.input.on(
      Phaser.Input.Events.POINTER_DOWN,
      (pointer: Phaser.Input.Pointer, gameObjects: Phaser.GameObjects.GameObject[]) => {
        // Determine focus by *what was actually hit*, not by current scene
        // state. A modal close button destroys its modal mid-handler, so by
        // the time we run the modal might already be gone — but gameObjects[0]
        // still references the element that was clicked. Walk its parent
        // chain to see if it lives inside an app window. If yes → focus that
        // window. If it's a modal / teach panel / menu bar / taskbar / empty
        // space → don't change focus.
        const apps = [this.inboxApp, this.slackApp, this.spreadsheetApp, this.fieldGuideApp];
        if (gameObjects.length > 0) {
          let cur: Phaser.GameObjects.GameObject | null = gameObjects[0];
          while (cur) {
            const owner = apps.find((a) => a === cur);
            if (owner) {
              this.bus.emit('window:focus-requested', { windowId: owner.windowId });
              return;
            }
            cur = (cur as { parentContainer?: Phaser.GameObjects.Container | null })
              .parentContainer ?? null;
          }
          // Hit something, but it wasn't inside an app — modal, taskbar, etc.
          return;
        }

        // Empty-space click: fall back to the bounds-contains check so a
        // click on a window's body (without an interactive child) still
        // promotes that window to the front.
        const containing = apps.filter((app) => app.containsPoint(pointer.x, pointer.y));
        if (containing.length === 0) return;
        containing.sort((a, b) => b.depth - a.depth);
        this.bus.emit('window:focus-requested', { windowId: containing[0].windowId });
      },
    );

    this.bus.on('day:ended', ({ realElapsedMs }) => {
      AudioManager.getInstance().playFiveOClockSilence();
      AudioManager.getInstance().stopAmbient();
      const snap = this.sessionTracker.snapshot();
      // Record run completion to persistent save before scene transitions.
      const dso = snap.scorecard.dsoAtClose ?? this.sessionTracker.computeDsoSnapshot();
      const craftScore =
        snap.scorecard.dunningRightBucket * 10 +
        snap.scorecard.cashCorrectCustomer * 15 +
        snap.scorecard.shortPaysResolved * 10 +
        snap.scorecard.disputesLogged * 8 +
        snap.scorecard.promiseToPaySolid * 12 +
        snap.scorecard.promiseToPaySoft * 4 +
        snap.scorecard.writeOffsApproved * 5;
      this.saveStore.recordRunComplete(craftScore, dso);
      this.scene.start('EndingScene', {
        realElapsedMs,
        emails: this.inboxApp.allEmails,
        termUnlocks: snap.termUnlocks,
        scorecard: snap.scorecard,
      });
    });

    this.clock.start();
    this.procedural.start();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  update(): void {
    this.clock.tick();
    this.procedural.tick();
    this.jamie.tick(this.clock.fictionalMinutes);
    this.cfo.tick(this.clock.fictionalMinutes);
    this.beats.tick(this.clock.fictionalMinutes);
    this.scriptedEmails.tick(this.clock.fictionalMinutes);

    // Endless-mode end conditions per spec §8: DSO>70, inbox>100, aging-90+ exceeds $100K.
    if (this.runMode === 'endless' && !this.endlessEnded) {
      const unread = this.inboxApp.unreadCount;
      if (unread > 100) {
        this.endlessEnded = true;
        this.bus.emit('endless:ended', { reason: 'unread', value: unread });
        this.bus.emit('day:ended', { realElapsedMs: this.time.now, unreadCount: unread });
      }
    }
  }

  private teardown(): void {
    this.procedural.stop();
    this.sessionTracker.destroy();
    this.windowManager.destroy();
    this.bus.clear();
  }
}
